import {useCallback, useMemo} from 'react'
import { useBrowerStorage } from '../useBrowserStorage'
import { COMPETITIONS_BOTTOM_BAR_CONFIG } from '../../config/competitionsBottomBarConfig'
import { useClearUserCompetitionCache } from './useClearUserCompetitionCache'
import {isNumeric} from "../../shared/utils";

const EMPTY_ARRAY = []
const FAVORITE_COMPETITIONS_VALIDATION = (x: any) =>
    Array.isArray(x) && x.every((v) => {
        if (typeof v !== "string") return false;
        const arrayValues = v.split(",");
        if (arrayValues.length !== 2) return false;
        return isNumeric(arrayValues[0]) && isNumeric(arrayValues[1]);
    })

const toFavoritePouleString = ({extPouleId, extCompetitionId}: {
    extPouleId: number,
    extCompetitionId: number
}) => `${extPouleId},${extCompetitionId}`

export const useFavoriteCompetitions = (): {
    favoritesPoules: { extPouleId: number; extCompetitionId: number }[]
    addFavorite: (params: { extPouleId: number, extCompetitionId: number }) => void
    removeFavorite: (params: { extPouleId: number, extCompetitionId: number }) => void
} => {
    const {value: localFavoritesPoules, setValue: setFavorites} = useBrowerStorage<string[]>({
        key: COMPETITIONS_BOTTOM_BAR_CONFIG.FAVORITE.LOCAL_STORAGE_KEY,
        defaultValue: EMPTY_ARRAY,
        getItemValidation: FAVORITE_COMPETITIONS_VALIDATION,
    })

    const { clearUserCompetitionCache } = useClearUserCompetitionCache()

    const addFavorite = useCallback(
        ({extPouleId, extCompetitionId}: { extPouleId: number, extCompetitionId: number }) => {
            setFavorites((p) =>
                p.includes(toFavoritePouleString({
                    extPouleId,
                    extCompetitionId
                })) ? p : [toFavoritePouleString({extPouleId, extCompetitionId}), ...p],
            )
            // reset cached user competitions
            clearUserCompetitionCache()
        },
        [clearUserCompetitionCache, setFavorites],
    )

    const removeFavorite = useCallback(
        ({extPouleId, extCompetitionId}: { extPouleId: number, extCompetitionId: number }) => {
            setFavorites((p) =>
                p.includes(toFavoritePouleString({extPouleId, extCompetitionId}))
                    ? p.filter((x) => x !== toFavoritePouleString({extPouleId, extCompetitionId}))
                    : p,
            )
            // reset cached user competitions
            clearUserCompetitionCache()
        },
        [clearUserCompetitionCache, setFavorites],
    )

    const favoritesPoules = useMemo(() => localFavoritesPoules.map(localFavoritePoule => {
        const [extPouleId, extCompetitionId] = localFavoritePoule.split(",");
        return {
            extPouleId: Number(extPouleId),
            extCompetitionId: Number(extCompetitionId)
        }
    }), [localFavoritesPoules])


    return {favoritesPoules, addFavorite, removeFavorite}
}
